import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  GraduationCap, 
  Zap, 
  History, 
  ArrowRight, 
  AlertCircle,
  CheckCircle2,
  Layers
} from 'lucide-react';

// Import the COEHeader component
import COEHeader from './COEHeader';

// ============================================================================
// RE-DESIGNED PROMOTION METHOD CARD
// ============================================================================
const PromotionCard = ({ 
  title, 
  description, 
  features, 
  badgeText, 
  badgeStyle, 
  onTap, 
  icon: CardIcon,
  isRecommended = false
}) => {
  return (
    <div 
      onClick={onTap}
      className={`group relative flex flex-col justify-between rounded-2xl border transition-all duration-200 cursor-pointer bg-white p-6 hover:shadow-xl ${
        isRecommended 
          ? 'border-emerald-500 ring-4 ring-emerald-50' 
          : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      {badgeText && (
        <span className={`absolute top-4 right-4 rounded-full px-2.5 py-0.75 text-[10px] font-bold uppercase tracking-wider text-white shadow-sm ${badgeStyle || 'bg-emerald-600'}`}>
          {badgeText}
        </span>
      )}

      <div>
        {/* Academic Icon Block */}
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl mb-5 transition-transform group-hover:scale-105 ${
          isRecommended ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'
        }`}>
          <CardIcon className="h-6 w-6" />
        </div>

        {/* Content */}
        <h3 className="text-lg font-bold text-slate-900 group-hover:text-emerald-700 transition-colors">
          {title}
        </h3>
        <p className="mt-2 text-sm text-slate-500 leading-relaxed">
          {description}
        </p>

        {/* Requirements & Features Checklist */}
        {features && features.length > 0 && (
          <ul className="mt-5 space-y-2.5 border-t border-slate-100 pt-4">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2.5 text-xs text-slate-600 leading-relaxed">
                <CheckCircle2 className={`h-4 w-4 mt-0.5 shrink-0 ${isRecommended ? 'text-emerald-500' : 'text-slate-400'}`} />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Action Button */}
      <div className="mt-6">
        <button
          type="button"
          className={`w-full flex items-center justify-center gap-2 rounded-xl py-3 px-4 text-center text-sm font-semibold text-white transition-all shadow-sm ${
            isRecommended ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-800 hover:bg-slate-900'
          }`}
        >
          <span>Configure Promotion</span>
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN STUDENT PROMOTION DASHBOARD
// ============================================================================
const PromoteStudents = ({ tenantId, user, orgInfo, tenant }) => {
  const navigate = useNavigate();

  const handleRegularPromotion = () => {
    navigate('/coe/student-promotion/regular', {
      state: { tenantId, user, mode: 'regular' }
    });
  };

  const handleDirectPromotion = () => {
    navigate('/coe/student-promotion/direct', {
      state: { tenantId, user, mode: 'direct' }
    });
  };

  const handleViewResults = () => {
    navigate('/coe/student-promotion/batches', {
      state: { tenantId, user, mode: 'view' }
    });
  };

  const handlePromotionBatches = () => {
    navigate('/coe/promotion-batches', {
      state: { tenantId, user }
    });
  };

  const promotionOptions = [
    {
      title: 'Academic Rule-Based Promotion',
      description: 'Evaluate overall student marks, attendance percentages, and credit thresholds against your institution\'s policy configuration.',
      features: [
        'Automated pass/fail and backlog verification',
        'Generates eligible lists and conditional hold-backs',
        'Safely archives final grades before advancing terms',
        'Flags students missing mandatory assessment components'
      ],
      icon: GraduationCap,
      badgeText: 'Recommended',
      badgeStyle: 'bg-emerald-600',
      isRecommended: true,
      onTap: handleRegularPromotion
    },
    {
      title: 'Mass Direct Promotion',
      description: 'Instantly advance whole classes or cohorts to the next academic year. Bypass grade prerequisites for bulk administrative overrides.',
      features: [
        'Ideal for foundational years or un-graded semesters',
        'Processes thousands of student enrollments instantly',
        'Allows backlog/grade clearance rules to be settled later',
        'Bypasses exam branch clearance protocols'
      ],
      icon: Zap,
      badgeText: 'Fast Track',
      badgeStyle: 'bg-indigo-600',
      isRecommended: false,
      onTap: handleDirectPromotion
    },
    {
      title: 'Promotion Logs & Rollbacks',
      description: 'Audit historical academic term changes, inspect student list variations, or revert mistaken year-end advancements.',
      features: [
        'Row-by-row tracking of student enrollment changes',
        'Searchable logs by Academic Year, Batch, and Section',
        'One-click reversion workflow for accidental promotions',
        'Download printable reports for university compliance records'
      ],
      icon: History,
      badgeText: 'Audit Trails',
      badgeStyle: 'bg-amber-600',
      isRecommended: false,
      onTap: handleViewResults
    },
    {
      title: '📋 Promotion Batches',
      description: 'View, filter, and manage all promotion batches. Review batch details, student decisions, and statuses across all academic years.',
      features: [
        'Filter batches by Program Type, Program, and Academic Year',
        'View batch summaries with promotion rates and student counts',
        'Drill down into individual batch details with student-wise decisions',
        'Track published, promoted, and draft batch statuses'
      ],
      icon: Layers,
      badgeText: 'New',
      badgeStyle: 'bg-indigo-600',
      isRecommended: false,
      onTap: handlePromotionBatches
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 antialiased font-sans">
      {/* Dynamic Global Dashboard Header */}
      <COEHeader orgInfo={orgInfo} tenant={tenant} />

      <main className="mx-auto max-w-7xl px-6 py-8">
        {/* Simple Section Context Message */}
        <div className="mb-8 border-b border-slate-200 pb-4">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Student Lifecycle Operations
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Configure term transitions, handle batch class advancements, or review academic logs.
          </p>
        </div>

        {/* Primary Action Options Layout */}
        <section className="mb-8">
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">
            Select Promotion Workflow
          </h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {promotionOptions.map((option, idx) => (
              <PromotionCard key={idx} {...option} />
            ))}
          </div>
        </section>

        {/* Quick Informational Assistant Panel */}
        <section>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="h-5 w-5 text-indigo-600" />
              <h3 className="text-sm font-bold text-slate-800">Academic Decision Matrix</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-slate-600 leading-relaxed">
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <span className="font-bold text-slate-800 block mb-1">When to use Rule-Based Promotion?</span>
                Use this option if the current semester has completely concluded, final grades/CGPA calculations have been finalized, and you want the system to isolate students with standing backlogs according to institution policy.
              </div>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <span className="font-bold text-slate-800 block mb-1">When to use Mass Direct Promotion?</span>
                Use this track for structural adjustments (e.g., migrating students to provisional semesters ahead of supplementary exam declarations) or for administrative tracking where grading curves do not block enrollment steps.
              </div>
            </div>

            <div className="mt-4 rounded-xl bg-amber-50 p-3 text-xs font-medium text-amber-900 border border-amber-200 flex items-center gap-2">
              <span className="text-base">⚠️</span>
              <span><strong>Compliance Notice:</strong> All student promotion updates permanently affect student transcripts and registration metrics. Ensure your current active Academic Calendar setup matches the target year configuration before execution.</span>
            </div>
          </div>
        </section>
      </main>

      {/* Simplified Clear Footer */}
      <footer className="mt-16 border-t border-slate-200 bg-white py-6 text-center text-xs font-medium text-slate-400">
        Campus Information System • Student Lifecycle Engine v2.5
      </footer>
    </div>
  );
};

export default PromoteStudents;